import { set } from 'mobx';
import { type Instance, types, getParentOfType } from 'mobx-state-tree';

import { getQuestionnaireAnswerFacetCount } from '../../../App/AppPageBlock/utils/getQuestionnaireAnswerFacetCount';
import { ANSWER_TYPE } from '../../../constants/roleRequirementsTypes';

import {
  type IQuestionnaireAnswer,
  type IQuestionnaireQuestion,
  QuestionnaireAnswer,
  QuestionnaireQuestion,
  type IQuestionnaireStore,
} from './../../JobStore/QuestionnaireStore';
import { FilterOperator, type RoleRequirementFilter } from './../types';
import { transformQuestionnaireIntoFilterQuestions } from './mappers';

const StandardFilterAnswer = types
  .model({
    answerInfo: QuestionnaireAnswer,
    isSelected: types.optional(types.boolean, false),
  })
  .views((self) => ({
    isDisabled({ count }: { count: number }): boolean {
      return count === 0 && !self.isSelected;
    },
  }))
  .actions((self) => ({
    onToggleAnswerSelected: () => {
      self.isSelected = !self.isSelected;
      const question = getParentOfType(self, FilterQuestion);
      question.onToggleAnswers();
    },
  }));

export type IStandardFilterAnswer = Instance<typeof StandardFilterAnswer>;

const SimplifiedFilterAnswer = types
  .model({
    answerInfo: QuestionnaireAnswer,
    isSelected: types.optional(types.boolean, false),
  })
  .views((self) => ({
    isDisabled({ count }: { count: number }): boolean {
      return count === 0 && !self.isSelected;
    },
  }))
  .actions((self) => ({
    onToggleAnswerSelected: () => {
      self.isSelected = !self.isSelected;
      const question = getParentOfType(self, FilterQuestion);
      self.answerInfo.answerGlobalIdsOrIds?.forEach(
        (hardcodedAnswerId: string) => {
          const answer = question.answers.find(
            (item) =>
              item.answerInfo.globalId === hardcodedAnswerId ||
              item.answerInfo.id === hardcodedAnswerId,
          );
          answer?.onToggleAnswerSelected();
        },
      );
      question.onToggleAnswers();
    },
  }));

export const FilterQuestion = types
  .model({
    questionInfo: QuestionnaireQuestion,
    answers: types.array(StandardFilterAnswer),
    isSelected: types.optional(types.boolean, false),
    isExpanded: types.optional(types.boolean, true),
    showPreferredAnswersMessage: types.optional(types.boolean, false),
    totalCount: types.optional(types.number, 0),
    simplifiedAnswers: types.array(SimplifiedFilterAnswer),
  })
  .views((self) => ({
    get selectedAnswers(): IQuestionnaireAnswer[] {
      return self.answers
        .filter((a) => a.isSelected)
        .map((answer) => answer.answerInfo);
    },
    get answersToDisplay() {
      return self.simplifiedAnswers.length > 0
        ? self.simplifiedAnswers
        : self.answers;
    },
    get hasSimplifiedAnswers() {
      return self.simplifiedAnswers.length > 0;
    },
    get hasPreferredAnswers() {
      return (
        this.answersToDisplay.filter((answer) => answer.answerInfo.isPreferred)
          .length > 0
      );
    },
    get hasAnyNonPreferredAnswersSelected() {
      return (
        this.answersToDisplay.filter(
          (answer) => !answer.answerInfo.isPreferred && answer.isSelected,
        ).length > 0
      );
    },
    get hasAllPreferredAnswersSelected() {
      if (this.hasAnyNonPreferredAnswersSelected) {
        return false;
      }
      return this.answersToDisplay
        .filter((answer) => answer.answerInfo.isPreferred)
        .every((answer) => answer.isSelected);
    },
    /**
     * isQuestionDisabled
     * @description Check whether a question is disabled, by checking whether all its answers are disabled
     * Note: we are naming this `isQuestionDisabled` to make it easier to distinguish between the `answer.isDisabled` functions.
     */
    isQuestionDisabled({
      questionnaireAnswerFacets,
    }: {
      questionnaireAnswerFacets: Record<string, { id: string; count: number }>;
    }): boolean {
      return this.answersToDisplay.every((answer) => {
        const facetCount = getQuestionnaireAnswerFacetCount({
          answerId: answer.answerInfo.globalIdOrId,
          questionnaireAnswerFacets,
        });

        // If facetCount===null, it means it's loading, or there are error fetching facets, we enable the question so hirer can interact with the UI
        // i.e. facet error should not block filtering.
        if (facetCount === null) {
          return false;
        }

        return facetCount === 0 && !answer.isSelected;
      });
    },
    get minOrderSelected() {
      if (self.answers.length === 0) {
        return -1;
      }
      return (
        self.answers.find((answer) => answer.isSelected)?.answerInfo.order ?? 1
      );
    },
    get maxOrderSelected() {
      if (self.answers.length === 0) {
        return -1;
      }
      const reversedAnswers = self.answers.slice().reverse();
      const lastAnswer = self.answers[self.answers.length - 1];
      return (
        reversedAnswers.find((answer) => answer.isSelected)?.answerInfo.order ??
        lastAnswer.answerInfo.order
      );
    },
    getSelectedAnswersCount({
      questionnaireAnswerFacets,
    }: {
      questionnaireAnswerFacets: Record<string, { id: string; count: number }>;
    }) {
      const selectedAnswers = self.answers.filter((a) => a.isSelected);

      const hasSelectedAnswers = selectedAnswers.length > 0;

      const answersToCount = hasSelectedAnswers
        ? selectedAnswers
        : self.answers;

      return answersToCount.reduce((total, answer) => {
        const answerCount =
          getQuestionnaireAnswerFacetCount({
            answerId: answer.answerInfo.globalIdOrId,
            questionnaireAnswerFacets,
          }) || 0;
        return total + answerCount;
      }, 0);
    },
  }))
  .actions((self) => ({
    onToggleQuestionSelected: ({
      questionnaireAnswerFacets,
    }: {
      questionnaireAnswerFacets: Record<string, { id: string; count: number }>;
    }) => {
      if (self.isSelected) {
        self.isSelected = !self.isSelected;
        self.showPreferredAnswersMessage = false;
        self.answersToDisplay
          .filter((a) => a.isSelected)
          .forEach((answer) => {
            answer.onToggleAnswerSelected();
          });
      } else {
        self.isSelected = !self.isSelected;
        self.isExpanded = true;

        const hasSelectablePreferredAnswers =
          self.answersToDisplay.filter((answer) => {
            const facetCount = getQuestionnaireAnswerFacetCount({
              answerId: answer.answerInfo.globalIdOrId,
              questionnaireAnswerFacets,
            });

            return (
              answer.answerInfo.isPreferred &&
              (facetCount === null || facetCount > 0)
            );
          }).length > 0;

        if (hasSelectablePreferredAnswers) {
          self.answersToDisplay.forEach((answer) => {
            const facetCount = getQuestionnaireAnswerFacetCount({
              answerId: answer.answerInfo.globalIdOrId,
              questionnaireAnswerFacets,
            });

            if (
              answer.answerInfo.isPreferred &&
              (facetCount === null || facetCount > 0)
            ) {
              answer.onToggleAnswerSelected();
            }
          });
          self.showPreferredAnswersMessage = true;
        } else {
          self.answersToDisplay.forEach((answer) => {
            const facetCount = getQuestionnaireAnswerFacetCount({
              answerId: answer.answerInfo.globalIdOrId,
              questionnaireAnswerFacets,
            });

            if (facetCount === null || facetCount > 0) {
              answer.onToggleAnswerSelected();
            }
          });
        }
      }
    },
    onToggleAnswers: () => {
      self.isSelected = self.answersToDisplay.some(
        (answer) => answer.isSelected,
      );
      self.showPreferredAnswersMessage = false;
    },
    onToggleQuestionExpanded: () => {
      self.isExpanded = !self.isExpanded;
    },
    onToggleSimplifiedAnswersSelected: (answerIds: string[]) => {
      answerIds.forEach((answerId: string) =>
        self.answers.forEach((answer) => {
          if (answer.answerInfo.id === answerId) {
            answer.onToggleAnswerSelected();
          }
        }),
      );
    },
    selectOnlyAnswersInRange: ({
      minOrder,
      maxOrder,
      questionnaireAnswerFacets,
    }: {
      minOrder: number;
      maxOrder: number;
      questionnaireAnswerFacets: Record<
        string,
        { id: string; count: number }
      > | null;
    }) => {
      self.isSelected = true;
      self.showPreferredAnswersMessage = false;

      self.answers.forEach((a) => {
        const facetCount = getQuestionnaireAnswerFacetCount({
          answerId: a.answerInfo.globalIdOrId,
          questionnaireAnswerFacets,
        });

        const isEnabled =
          facetCount !== null
            ? !a.isDisabled({
                count: facetCount,
              })
            : true; // When facetCount===null, it's loading or there are errors fetching facet. We enable all the range filter answers so user can choose options, instead of being stuck with disabled UI

        a.isSelected =
          a.answerInfo.order >= minOrder &&
          a.answerInfo.order <= maxOrder &&
          isEnabled;
      });
    },
    clearSelectedAnswers: () => {
      self.answers.forEach((answer) => {
        answer.isSelected = false;
      });
      self.simplifiedAnswers.forEach((answer) => {
        answer.isSelected = false;
      });
    },
  }));

export type IFilterQuestion = Instance<typeof FilterQuestion>;

const FiltersStore = types
  .model({
    questions: types.optional(types.array(FilterQuestion), []),
  })
  .views((self) => ({
    get selectedQuestionAnswers() {
      const rrFilters: Array<{
        question: IQuestionnaireQuestion;
        answers: IQuestionnaireAnswer[];
      }> = [];

      self.questions.forEach((question) => {
        const selectedAnswers = question.selectedAnswers;
        if (selectedAnswers.length > 0) {
          rrFilters.push({
            question: question.questionInfo,
            answers: selectedAnswers,
          });
        }
      });
      return rrFilters;
    },
    get selectedRrFilters(): RoleRequirementFilter[] {
      const rrGlobalFilters: RoleRequirementFilter[] = [];

      this.selectedQuestionAnswers.forEach(({ question, answers }) => {
        if (answers.length > 0) {
          rrGlobalFilters.push({
            questionId: question.globalIdOrId,
            operator:
              question.answerType === ANSWER_TYPE.MULTISELECT
                ? FilterOperator.AND
                : FilterOperator.OR,
            answerIds: answers.map((answer) => answer.globalIdOrId),
          });
        }
      });
      return rrGlobalFilters;
    },
    get hasSelectedFilter() {
      return this.selectedRrFilters.length > 0;
    },
    get hasFilterQuestions() {
      return self.questions.length > 0;
    },
    get isMustHaveToggleSelected() {
      return self.questions.every((question) => {
        if (question.questionInfo.essential) {
          return question.hasAllPreferredAnswersSelected;
        }
        return !question.isSelected;
      });
    },
  }))
  .actions((self) => ({
    clearFilters: () => {
      self.questions.forEach((question) => {
        question.isSelected = false;
        question.showPreferredAnswersMessage = false;
        question.clearSelectedAnswers();
      });
    },
    load: (questionnaire: IQuestionnaireStore) => {
      const questions =
        transformQuestionnaireIntoFilterQuestions(questionnaire);
      set(self, { questions });
    },
  }))
  .actions((self) => ({
    enableMustHaveFilter: () => {
      self.clearFilters();
      self.questions
        .filter((question) => question.questionInfo.essential)
        .forEach((question) => {
          question.isSelected = true;
          question.isExpanded = true;
          question.showPreferredAnswersMessage = true;
          question.answersToDisplay.forEach((answer) => {
            if (answer.answerInfo.isPreferred) {
              answer.onToggleAnswerSelected();
            }
          });
        });
    },
  }))
  .actions((self) => ({
    toggleMustHaveFilter: (): 'selected' | 'unselected' => {
      if (self.isMustHaveToggleSelected) {
        self.clearFilters();
        return 'unselected';
      }
      self.enableMustHaveFilter();
      return 'selected';
    },
  }));

export type IFiltersStore = Instance<typeof FiltersStore>;
export default FiltersStore;
